import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ConversionType } from '@xing-com/crate-common-graphql-types';
import type {
  ConvertedReplySettingsJob,
  Maybe,
} from '@xing-com/crate-common-graphql-types';
import { XING_TERMS_PATH } from '@xing-com/crate-core-routes';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useExperiment } from '@xing-com/hub';
import { Hyperlink } from '@xing-com/hyperlink';
import { OnlyOnLarge } from '@xing-com/layout-primitives';
import { BodyCopy } from '@xing-com/typography';

import { ExternalDocuments } from '../external-documents';
import type { ExternalDocWithOtherType } from '../types';

export const XING_PRIVACY_LINK_DE =
  'https://privacy.xing.com/de/datenschutzerklaerung';

export const XING_PRIVACY_LINK_EN =
  'https://privacy.xing.com/en/privacy-policy';

export const XING_APPLICATION_PRIVACY_DE =
  'https://privacy.xing.com/de/datenschutzerklaerung/informationen-die-sie-uns-mitteilen/bewerbungen-uber-xing';

export const XING_APPLICATION_PRIVACY_EN =
  'https://privacy.xing.com/en/privacy-policy/information-you-provide-us-with/applications-via-xing';

const PrivacyLink = styled(Hyperlink)`
  text-decoration: none;
`;

type Props = {
  externalDocuments?: Maybe<ExternalDocWithOtherType>[];
  replySettings?: Maybe<Omit<ConvertedReplySettingsJob, 'id'>>;
};

export const LegalInfo: React.FC<Props> = ({
  externalDocuments,
  replySettings,
}) => {
  const { isLoggedIn } = useLoginState();
  const { locale } = useIntl();
  const variant = useExperiment('ABACUS-575');
  const showConvertedJobEmailInfo =
    replySettings?.conversionType ===
      ConversionType.UrlTypeWithDescriptionEmail && replySettings.converted;
  const privacyUrl =
    locale === 'de' ? XING_PRIVACY_LINK_DE : XING_PRIVACY_LINK_EN;
  const applicationPrivacyUrl =
    locale === 'de' ? XING_APPLICATION_PRIVACY_DE : XING_APPLICATION_PRIVACY_EN;

  return (
    <>
      {showConvertedJobEmailInfo ? (
        <BodyCopy>
          <FormattedMessage
            id="JOBS_POSTING_XING_APPLY_INFO_SENDER_EMAIL_ADDRESS"
            values={{ replyEmail: replySettings.replyEmail }}
          />{' '}
        </BodyCopy>
      ) : null}
      {!isLoggedIn ? (
        <>
          <BodyCopy lineHeight="dense" data-testid="xing-legal-info">
            <FormattedMessage
              id="JOBS_INSTANT_APPLY_FORM_LO_XING_TERMS_AND_PRIVACY"
              values={{
                a1: (chunks) => (
                  <Hyperlink
                    href={XING_TERMS_PATH}
                    target="_blank"
                    rel="noopener"
                  >
                    {chunks}
                  </Hyperlink>
                ),
                a2: (chunks) => (
                  <Hyperlink href={privacyUrl} target="_blank" rel="noopener">
                    {chunks}
                  </Hyperlink>
                ),
              }}
            />
          </BodyCopy>{' '}
        </>
      ) : variant === 'B' ? (
        <>
          <OnlyOnLarge>
            <BodyCopy data-testid="xing-legal-info-long">
              <FormattedMessage id="JOBS_INSTANT_APPLY_FORM_LEGAL_COPY_LONG" />
            </BodyCopy>
          </OnlyOnLarge>
          <BodyCopy data-testid="xing-legal-info-short">
            <FormattedMessage
              id="JOBS_INSTANT_APPLY_FORM_LEGAL_COPY_SHORT"
              values={{
                privacyNoticeLink: (
                  <PrivacyLink
                    href={applicationPrivacyUrl}
                    target="_blank"
                    rel="noopener"
                  >
                    <FormattedMessage id="JOBS_INSTANT_APPLY_FORM_LEGAL_COPY_PRIVACY_NOTICE" />
                  </PrivacyLink>
                ),
              }}
            />
          </BodyCopy>
        </>
      ) : null}
      <ExternalDocuments externalDocuments={externalDocuments} />
    </>
  );
};
